import { useState } from "react";
import { useNavigate } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import { useCreateGrow, useGrowsQuery } from "../hooks/useGrowsQuery.js";
import DeviceSelect from "../components/DeviceSelect.jsx";
import { useInventoryQuery } from "../hooks/useInventoryQuery.js";
import usePlan from "../hooks/usePlan.js";

const PLAN_LIMITS = { hobby: 1, pro: 10, enterprise: Infinity };

function UpgradeModal({ current, limit, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Grow limit reached</h2>
        <p className="text-sm text-gray-600 mb-4">
          You've reached your Hobby plan limit ({current}/{limit} grows).<br />
          To upgrade to Pro, email us at{" "}
          <a href="mailto:hello@fungistics.com" className="text-cyan-600 underline">hello@fungistics.com</a>.
        </p>
        <button
          onClick={onClose}
          className="w-full rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
        >
          Got it
        </button>
      </div>
    </div>
  );
}

const MUSHROOM_SPECIES = [
  "Vader (Psilocybe cubensis)",
  "Makilla Gorilla (Psilocybe cubensis)",
  "Trinity (Psilocybe cubensis)",
  "African Transkei (Psilocybe cubensis)",
  "Albedo (Psilocybe cubensis)",
  "Tidal Wave (Psilocybe cubensis)",
  "Super Strain Koh Samui (Psilocybe cubensis)",
  "Hillbilly (Psilocybe cubensis)",
  "PE6 (Psilocybe cubensis)",
  "Bluey Vuitton (Psilocybe cubensis)",
  "Pleurotus ostreatus",
  "Pleurotus eryngii",
  "Lentinula edodes",
  "Grifola frondosa",
  "Hericium erinaceus",
  "Agaricus bisporus",
];

const SUBSTRATES = [
  "Straw",
  "Sawdust",
  "Masters Mix",
  "Soy Hull Pellets",
  "Cardboard",
  "Coffee Grounds",
  "Coco Coir",
  "Manure/Compost",
];

export default function GrowNew() {
  const navigate = useNavigate();
  const createGrow = useCreateGrow();
  const { data: inventoryItems } = useInventoryQuery();
  const { plan, planLoading } = usePlan();
  const { data: grows } = useGrowsQuery();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const [name, setName] = useState("");
  const [speciesInput, setSpeciesInput] = useState("");
  const [substrate, setSubstrate] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [inventoryItemId, setInventoryItemId] = useState("");
  const [error, setError] = useState(null);

  const growCount = grows?.length ?? 0;
  const limit = PLAN_LIMITS[plan ?? "hobby"] ?? 1;
  const atLimit = !planLoading && growCount >= limit;

  const readyItems = (inventoryItems ?? []).filter((i) => i.status === "ready");
  const selectedItem = readyItems.find((i) => i.id === Number(inventoryItemId)) ?? null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const body = {
      name: name.trim(),
      crop_type: "mushroom",
    };
    if (speciesInput.trim()) body.species = speciesInput.trim();
    if (substrate) body.substrate = substrate;
    if (deviceId) body.device_id = Number(deviceId);
    if (inventoryItemId) body.inventory_item_id = Number(inventoryItemId);

    try {
      const grow = await createGrow.mutateAsync(body);
      navigate(`/grows/${grow.id}`);
    } catch (err) {
      setError(err.message ?? "Failed to create grow");
    }
  }

  return (
    <LayoutWide title="New Grow">
      {showUpgrade && (
        <UpgradeModal current={growCount} limit={limit} onClose={() => setShowUpgrade(false)} />
      )}
      <div className="max-w-lg mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/grows")}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Back"
          >
            ←
          </button>
          <h1 className="text-2xl font-semibold text-cyan-800">New Grow</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grow name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Oyster batch #4"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Species
            </label>
            <input
              type="text"
              list="species-list"
              value={speciesInput}
              onChange={(e) => setSpeciesInput(e.target.value)}
              placeholder="e.g. Pleurotus ostreatus"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <datalist id="species-list">
              {MUSHROOM_SPECIES.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Substrate
            </label>
            <select
              value={substrate}
              onChange={(e) => setSubstrate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
            >
              <option value="">— select substrate —</option>
              {SUBSTRATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Use from inventory
            </label>
            {readyItems.length === 0 ? (
              <p className="text-xs text-gray-400">No inventory items with status "ready".</p>
            ) : (
              <select
                value={inventoryItemId}
                onChange={(e) => setInventoryItemId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              >
                <option value="">— enter manually —</option>
                {readyItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}{item.species ? ` · ${item.species}` : ""} ({item.quantity} {item.unit} remaining)
                  </option>
                ))}
              </select>
            )}
            {selectedItem && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                1 {selectedItem.unit.replace(/s$/, "")} will be deducted from <span className="font-medium">{selectedItem.name}</span> ({selectedItem.quantity} → {selectedItem.quantity - 1} remaining)
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Monitor device
            </label>
            <DeviceSelect value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">
              Link a sensor device to track live environmental data for this grow.
            </p>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {atLimit && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
              You've reached your {plan === "hobby" ? "Hobby" : "Pro"} plan limit ({growCount}/{limit} grows).{" "}
              <button type="button" onClick={() => setShowUpgrade(true)} className="underline font-medium">Learn more</button>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type={atLimit ? "button" : "submit"}
              onClick={atLimit ? () => setShowUpgrade(true) : undefined}
              disabled={(!atLimit && (createGrow.isPending || !name.trim())) || planLoading}
              className="flex-1 bg-cyan-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {planLoading ? "Loading…" : atLimit ? "Plan limit reached" : createGrow.isPending ? "Creating…" : "Create grow"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/grows")}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </LayoutWide>
  );
}
